import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Donor } from '../../../interfaces/donor';
import { Recipient } from '../../../interfaces/recipient';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { PagetoolbarComponent } from '../../pagetoolbar/pagetoolbar.component';
import { MatFormField } from '@angular/material/form-field';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { ParticipantService } from '../../../services/participant.service';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { Participant } from '../../../interfaces/participant';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Center } from '../../../interfaces/center';
import { CenterService } from '../../../services/center.service';
import { PairService } from '../../../services/pair.service';
import moment from 'moment';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { MatMomentDateModule, MomentDateAdapter } from '@angular/material-moment-adapter';
import { validatePhenotypepart } from '../../../utils/phenotype.utils';
import { AuthService } from '../../../auth/auth.service';
import { Permission } from '../../../interfaces/permission';

export const MY_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'DD/MM/YYYY'
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

@Component({
  selector: 'app-participant-edit',
  imports: [
    ReactiveFormsModule,
    MatSnackBarModule,
    MatIconModule,
    MatSelectModule,
    CommonModule,
    MatFormField,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogContent,
    MatNativeDateModule,
    MatDatepickerModule,
    MatOptionModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatMomentDateModule
  ],
  templateUrl: './participant-edit.component.html',
  styleUrl: './participant-edit.component.scss',
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }
  ]
})
export class ParticipantEditComponent {
  donorForm!: FormGroup;
  recipientForm!: FormGroup;
  formType: string = 'donor';

  //Send back new participant to list
  responseSubjectNewDonor: Subject<Donor> = new Subject<Donor>();
  responseSubjectNewRecipient: Subject<Recipient> = new Subject<Recipient>();
  recipient: Recipient | undefined;
  donor: Donor | undefined;
  centers!: Center[];
  assignableCenters!: Center[];
  privileges: boolean = true;
  donorPrivileges: boolean = true;
  isDonorPartOfPair: boolean = false;
  canEditPriority: boolean = false;
  canEditAcceptableHla: boolean = true;
  isCenterUser: boolean = false;
  isOperator: boolean = false;
  isAllocation: boolean = false;
  isCenter: boolean = false;
  initialPriorityValue: boolean = false;
  initialPrivilegesValue: boolean = false;
  initialDonorPrivilegesValue: boolean = false;
  constructor(
    private fb: FormBuilder,
    private participantService: ParticipantService,
    private centerService: CenterService,
    private pairService: PairService,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    public dialogRef: MatDialogRef<ParticipantEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { participant: Participant }
  ) {
    //Assign participant to corret form type.
    if (data.participant.type === 'Recipient') {
      this.formType = 'recipient';
      this.recipient = data.participant;
    } else if (data.participant.type === 'Donor') {
      this.donor = data.participant;
    }
  }

  ngOnInit(): void {
    this.donorForm = this.fb.group({});
    this.getAllCenters();
    this.privileges = this.getPrivileges();
    this.donorPrivileges = this.getDonorPrivileges();

    // Store initial values for priority and privileges
    this.initialPriorityValue = this.recipient?.priority || false;
    this.initialPrivilegesValue = this.getPrivileges();
    this.initialDonorPrivilegesValue = this.getDonorPrivileges();

    // Check if user can edit priority field
    // Logic: Centers can only REQUEST priority (not set it directly)
    // Only OPERATOR, ALLOCATION, and XOCOMMISSION can edit priority directly
    this.authService.getAllPermissions$().subscribe((permissions) => {
      const hasPriorityRequest = permissions.includes(Permission.PRIORITY_REQUEST);
      const hasPriorityApproval = permissions.includes(Permission.PRIORITY_APPROVAL);
      const hasPrivilegesAdd = permissions.includes(Permission.PRIVILEGES_ADD);
      const hasPrivilegesEdit = permissions.includes(Permission.PRIVILEGES_EDIT);
      const hasPrivilegesRemove = permissions.includes(Permission.PRIVILEGES_REMOVE);

      // Determine user role based on permissions
      // OPERATOR: has PRIORITY_APPROVAL and all PRIVILEGES permissions
      this.isOperator = hasPriorityApproval && hasPrivilegesAdd && hasPrivilegesEdit && hasPrivilegesRemove;

      // CENTER: has PRIORITY_REQUEST but not PRIORITY_APPROVAL
      // Centers have all PRIVILEGES permissions (ADD, EDIT, REMOVE) but can only remove in practice
      this.isCenter = hasPriorityRequest && !hasPriorityApproval;

      // ALLOCATION: has neither PRIORITY_REQUEST nor PRIORITY_APPROVAL
      // Allocation has only PRIVILEGES_VIEW, not PRIVILEGES_ADD/EDIT/REMOVE
      // This should be everyone who is NOT operator and NOT center
      this.isAllocation = !hasPriorityRequest && !hasPriorityApproval && !hasPrivilegesAdd;

      console.log('[Role Detection]', {
        isOperator: this.isOperator,
        isAllocation: this.isAllocation,
        isCenter: this.isCenter,
        hasPriorityRequest,
        hasPriorityApproval,
        hasPrivilegesAdd,
        hasPrivilegesEdit,
        hasPrivilegesRemove
      });


      // Center users have PRIORITY_REQUEST but not PRIORITY_APPROVAL
      // They should use "request priority" feature instead of directly editing
      this.canEditPriority = !hasPriorityRequest || hasPriorityApproval;

      // Disable priority field if user cannot edit it
      if (!this.canEditPriority && this.recipientForm) {
        this.recipientForm.get('priority')?.disable({ emitEvent: false });
      }

      // Determine if user is a center user (has PRIORITY_REQUEST but not PRIORITY_APPROVAL)
      this.isCenterUser = hasPriorityRequest && !hasPriorityApproval;

      // CENTER users can only edit "Delisted Unacceptables (Priority)" when the recipient has priority.
      // The actual enable/disable of the form control is applied in applyCenterPrivilegeFieldRestrictions()
      // once the form exists; here we just set the flag that drives the field's tooltip.
      if (this.isCenter && this.recipient) {
        this.canEditAcceptableHla = !!this.recipient.priority;
      }

      // Apply field restrictions AFTER role flags are set
      // Use setTimeout to ensure forms are created first (they're created synchronously after this subscription)
      setTimeout(() => {
        this.applyFieldRestrictions();
      }, 0);
    });

    // Split HLA into Class I and Class II for display
    const { hlaClassI, hlaClassII } = this.splitHla(this.donor?.hla || '');

    this.donorForm = this.fb.group({
      id: [this.donor?.id],
      dob: [this.donor?.dob ? new Date(this.donor.dob) : null, [Validators.required]],
      firstName: [this.donor?.firstName],
      lastName: [this.donor?.lastName],
      gender: [this.donor?.gender],
      center: [this.donor?.center, [Validators.required]],
      abo: [this.donor?.abo],
      hlaClassI: [hlaClassI],
      hlaClassII: [hlaClassII],
      enisNumber: [
        this.donor?.enisNumber,
        [
          Validators.required,
          Validators.minLength(5),
          Validators.maxLength(6),
          Validators.pattern(/^[0-9]*$/) // Ensure only digits are allowed
        ]
      ],
      altruist: [this.donor?.altruist],
      active: [this.donor?.active !== undefined ? this.donor.active : true],
      donorPrivileges: [this.getDonorPrivileges()],
      limitedToCenter: [], // Use mapped array
      donateToWaitlist: [this.donor?.donateToWaitlist !== undefined ? this.donor.donateToWaitlist : false]
    });

    this.recipientForm = this.fb.group({
      id: [this.recipient?.id],
      dob: [this.recipient?.dob ? new Date(this.recipient.dob) : null, [Validators.required]],
      firstName: [this.recipient?.firstName],
      lastName: [this.recipient?.lastName],
      gender: [this.recipient?.gender],
      center: [this.recipient?.center, [Validators.required]],
      abo: [this.recipient?.abo],
      abPhenotype: [this.recipient?.abPhenotype, this.createPhenotypeValidator()],
      drPhenotype: [this.recipient?.drPhenotype],
      urgency: [this.recipient?.urgency],
      primaryDiagnosis: [this.recipient?.primaryDiagnosis],
      dateFirstDialysis: [this.recipient?.dateFirstDialysis ? new Date(this.recipient.dateFirstDialysis) : null],
      dateScreening: [this.recipient?.dateScreening ? new Date(this.recipient.dateScreening) : null],
      unacceptables: [this.recipient?.unacceptables],
      previousPRA: [this.recipient?.previousPRA],
      virtualPRA: [this.recipient?.virtualPRA],
      priority: [this.recipient?.priority],
      privileges: [this.getPrivileges()],
      extraWaitTime: [this.recipient?.extraWaitTime],
      maxDonorAge: [this.recipient?.maxDonorAge],
      maxMismatches: [this.recipient?.maxMismatches],
      maxMmA: [this.recipient?.maxMmA],
      maxMmB: [this.recipient?.maxMmB],
      maxMmDR: [this.recipient?.maxMmDR],
      acceptableBloodTypes: [this.parseAcceptableBloodTypes(this.recipient?.acceptableBloodTypes)],
      acceptableHla: [this.recipient?.acceptableHla],
      lowMfi1: [this.recipient?.lowMfi1],
      lowMfi2: [this.recipient?.lowMfi2],
      enisNumber: [
        this.recipient?.enisNumber,
        [
          Validators.required,
          Validators.minLength(5),
          Validators.maxLength(6),
          Validators.pattern(/^[0-9]*$/) // Ensure only digits are allowed
        ]
      ]
    });

    // Disable priority field if user cannot edit it
    if (!this.canEditPriority) {
      this.recipientForm.get('priority')?.disable({ emitEvent: false });
    }

    this.recipientForm.get('extraWaitTime')?.valueChanges.subscribe(() => this.updatePrivilegesState());
    this.recipientForm.get('maxDonorAge')?.valueChanges.subscribe(() => this.updatePrivilegesState());
    this.recipientForm.get('maxMismatches')?.valueChanges.subscribe(() => this.updatePrivilegesState());
    this.recipientForm.get('maxMmA')?.valueChanges.subscribe(() => this.updatePrivilegesState());
    this.recipientForm.get('maxMmB')?.valueChanges.subscribe(() => this.updatePrivilegesState());
    this.recipientForm.get('maxMmDR')?.valueChanges.subscribe(() => this.updatePrivilegesState());
    this.recipientForm.get('acceptableBloodTypes')?.valueChanges.subscribe(() => this.updatePrivilegesState());
    this.recipientForm.get('acceptableHla')?.valueChanges.subscribe(() => this.updatePrivilegesState());
    this.recipientForm.get('lowMfi1')?.valueChanges.subscribe(() => this.updatePrivilegesState());
    this.recipientForm.get('lowMfi2')?.valueChanges.subscribe(() => this.updatePrivilegesState());

    this.donorForm.get('limitedToCenter')?.valueChanges.subscribe(() => this.updateDonorPrivilegesState());
    this.donorForm.get('donateToWaitlist')?.valueChanges.subscribe(() => this.updateDonorPrivilegesState());
    this.updatePrivilegesState();

    // Check if donor is part of a pair
    if (this.donor && this.donor.id) {
      this.checkIfDonorIsPartOfPair(this.donor.id);
    }
  }

  editDonor(): void {
    if (this.donorForm.invalid) {
      this.showNotification('Please fill out all required fields correctly.');
      return;
    }

    // No additional validation needed for CENTER users
    // They can add/remove donor privileges freely

    // Combine HLA Class I and Class II back into single HLA field
    // Use getRawValue() to include disabled fields in submission
    const rawValue = this.donorForm.getRawValue();
    const hla = this.combineHla(rawValue.hlaClassI, rawValue.hlaClassII);

    const donorData: Donor = {
      ...rawValue,
      hla: hla,
      dob: this.formatDate(rawValue.dob),
      // Ensure limitedToCenter is null if empty
      limitedToCenter: (rawValue.limitedToCenter && rawValue.limitedToCenter.length > 0) ? rawValue.limitedToCenter : null
    };

    // Remove the UI-only fields
    delete (donorData as any).hlaClassI;
    delete (donorData as any).hlaClassII;

    this.participantService.updateDonor(donorData).subscribe({
      next: (response: Donor) => {
        this.showNotification('Donor edited successfully!');
        // Map response (Donor) to a Participant with type "Donor"
        response.type = 'Donor';
        const participantDonor: Participant = {
          ...response
        };
        this.responseSubjectNewDonor.next(participantDonor);
        this.dialogRef.close(participantDonor); // Close the dialog and return updated donor
      },
      error: (err) => {
        const errorMessage = err?.error?.message || 'Failed to edit donor. Please try again.';
        this.showNotification(errorMessage);
      }
    });
  }
  private formatDate(date: string): string | null {
    if (!date) return null;
    let momentDate = moment(date);
    if (!moment.isMoment(momentDate)) {
      throw new Error('Invalid date');
    }
    const year = momentDate.year();
    const month = momentDate.format('MM'); // Use Moment.js method to format month
    const day = momentDate.format('DD'); // Use Moment.js method to format day

    return `${year}-${month}-${day}`;
  }

  editRecipient(): void {
    if (this.recipientForm.invalid) {
      this.showNotification('Please fill out all required fields correctly.');
      return;
    }

    // ALLOCATION: can only uncheck priority, not check it
    if (this.isAllocation) {
      const currentPriority = this.recipientForm.get('priority')?.value;
      if (currentPriority && !this.initialPriorityValue) {
        this.showNotification('Allocation users can only uncheck priority, not check it.');
        return;
      }
    }

    // No additional validation needed for CENTER users
    // They can add/remove all privileges; "Delisted Unacceptables (Priority)" is editable only when the recipient has priority

    // Use getRawValue() to include disabled fields in submission
    const formValue = this.recipientForm.getRawValue();

    // Convert empty strings to null for privilege fields
    const recipientData: Recipient = {
      ...formValue,
      dob: this.formatDate(formValue.dob),
      dateFirstDialysis: this.formatDate(formValue.dateFirstDialysis),
      dateScreening: this.formatDate(formValue.dateScreening),
      acceptableBloodTypes: this.formatAcceptableBloodTypes(formValue.acceptableBloodTypes),
      // Convert empty strings to null (except extraWaitTime which should be false)
      maxMismatches: formValue.maxMismatches || null,
      maxMmA: formValue.maxMmA || null,
      maxMmB: formValue.maxMmB || null,
      maxMmDR: formValue.maxMmDR || null,
      maxDonorAge: formValue.maxDonorAge || null,
      lowMfi1: (formValue.lowMfi1 && formValue.lowMfi1.trim() !== '') ? formValue.lowMfi1 : null,
      lowMfi2: (formValue.lowMfi2 && formValue.lowMfi2.trim() !== '') ? formValue.lowMfi2 : null,
      acceptableHla: (formValue.acceptableHla && formValue.acceptableHla.trim() !== '') ? formValue.acceptableHla : null,
      // extraWaitTime should be false, not null (database constraint)
      extraWaitTime: formValue.extraWaitTime || false
    };

    this.participantService.updateRecipient(recipientData).subscribe({
      next: (response: Recipient) => {
        this.showNotification('Recipient edited successfully!');
        // Map response (Recipient) to a Participant with type "Recipient"
        response.type = 'Recipient';
        const participantRecipient: Participant = {
          ...response
        };
        this.responseSubjectNewRecipient.next(participantRecipient);
        this.dialogRef.close(participantRecipient); // Close the dialog and return updated recipient
      },
      error: (err) => {
        const errorMessage = err?.error?.message || 'Failed to edit recipient. Please try again.';
        this.showNotification(errorMessage);
      }
    });
  }

  showNotification(message: string, action: string = 'Close'): void {
    this.snackBar.open(message, action, {
      duration: 3000,
      verticalPosition: 'top',
      horizontalPosition: 'center'
    });
  }

  private getAllCenters(): void {
    this.centerService.getAllCenters().subscribe({
      next: (data) => {
        this.centers = data;
        let selectedCenter;
        if (this.recipient) {
          selectedCenter = this.centers.find((c) => c.id === this.recipient?.center.id);
        } else {
          selectedCenter = this.centers.find((c) => c.id === this.donor?.center.id);
          // Map limitedToCenter to match references in centers array
          const mappedLimitedToCenter = this.donor?.limitedToCenter?.map((selectedCenter) => this.centers.find((center) => center.id === selectedCenter.id)) || [];
          this.donorForm.patchValue({ limitedToCenter: mappedLimitedToCenter });
        }

        // Filter out UNKNOWN center for assignment dropdown, but include it if it's the currently selected center
        // This allows users to see that UNKNOWN is currently assigned but prevents them from assigning UNKNOWN to new participants
        this.assignableCenters = this.centers.filter((center) => center.name !== 'UNKNOWN' || center.id === selectedCenter?.id);

        this.donorForm.patchValue({ center: selectedCenter });
        this.recipientForm.patchValue({ center: selectedCenter });
      },
      error: (error) => {
        console.log(error);
      }
    });
  }

  private getPrivileges(): boolean {
    if (
      this.recipient?.extraWaitTime ||
      this.recipient?.acceptableBloodTypes ||
      this.recipient?.acceptableHla ||
      this.recipient?.maxDonorAge ||
      this.recipient?.maxMismatches ||
      this.recipient?.maxMmA ||
      this.recipient?.maxMmB ||
      this.recipient?.maxMmDR ||
      this.recipient?.lowMfi1 ||
      this.recipient?.lowMfi2
    ) {
      return true;
    }
    return false;
  }

  private getDonorPrivileges(): boolean {
    if (this.donor?.limitedToCenter && this.donor.limitedToCenter.length > 0) {
      return true;
    }
    if (this.donor?.donateToWaitlist) {
      return true;
    }
    return false;
  }

  updatePrivilegesState(): void {
    // Use getRawValue to include disabled fields (important for CENTER users where privilege fields are disabled)
    const formValues = this.recipientForm.getRawValue();
    const extraWaitTime = formValues.extraWaitTime;
    const maxDonorAge = formValues.maxDonorAge;
    const maxMismatches = formValues.maxMismatches;
    const maxMmA = formValues.maxMmA;
    const maxMmB = formValues.maxMmB;
    const maxMmDR = formValues.maxMmDR;
    const acceptableBloodTypes = formValues.acceptableBloodTypes;
    const acceptableHla = formValues.acceptableHla;
    const lowMfi1 = formValues.lowMfi1;
    const lowMfi2 = formValues.lowMfi2;

    const privilegesControl = this.recipientForm.get('privileges')!;
    // Check if any privilege field has a value (acceptableBloodTypes is an array, so check length)
    // Treat empty strings as no value
    const hasPrivileges = extraWaitTime || maxDonorAge || maxMismatches || maxMmA || maxMmB || maxMmDR ||
                          (acceptableBloodTypes && acceptableBloodTypes.length > 0) ||
                          (acceptableHla && acceptableHla.trim() !== '') ||
                          (lowMfi1 && lowMfi1.trim() !== '') ||
                          (lowMfi2 && lowMfi2.trim() !== '');

    if (hasPrivileges) {
      privilegesControl.setValue(true, { emitEvent: false }); // Force it to stay checked
      // CENTER/ALLOCATION users: allow unchecking to remove privileges
      // OPERATOR: disable the checkbox (can't uncheck while privileges exist)
      if (this.isOperator) {
        privilegesControl.disable({ emitEvent: false });
      } else {
        // Enable for CENTER and ALLOCATION users so they can uncheck it
        privilegesControl.enable({ emitEvent: false });
      }
    } else {
      // No privileges exist
      privilegesControl.setValue(false, { emitEvent: false });
      // All users can check the privileges checkbox to add privileges
      privilegesControl.enable({ emitEvent: false });
    }

    // For CENTER users, update field-level restrictions based on which fields have values
    if (this.isCenter) {
      this.applyCenterPrivilegeFieldRestrictions();
    }
  }

  updateDonorPrivilegesState(): void {
    // Use getRawValue to include disabled fields (important for CENTER users where limitedToCenter is disabled)
    const formValues = this.donorForm.getRawValue();
    const limitedToCenter = formValues.limitedToCenter || [];
    const donateToWaitlist = formValues.donateToWaitlist || false;
    const privilegesControl = this.donorForm.get('donorPrivileges')!;

    const hasPrivileges = limitedToCenter.length > 0 || donateToWaitlist;

    if (hasPrivileges) {
      privilegesControl.setValue(true, { emitEvent: false }); // Force it to stay checked
      // CENTER/ALLOCATION users: allow unchecking to remove privileges
      // OPERATOR: disable the checkbox (can't uncheck while privileges exist)
      if (this.isOperator) {
        privilegesControl.disable({ emitEvent: false });
      } else {
        // Enable for CENTER and ALLOCATION users so they can uncheck it
        privilegesControl.enable({ emitEvent: false });
      }
    } else {
      // No privileges exist
      privilegesControl.setValue(false, { emitEvent: false });
      // All users can check the privileges checkbox to add privileges
      privilegesControl.enable({ emitEvent: false });
    }

    // For CENTER users, update field-level restrictions based on which fields have values
    if (this.isCenter) {
      this.applyCenterDonorPrivilegeFieldRestrictions();
    }
  }

  checkIfDonorIsPartOfPair(donorId: string): void {
    this.pairService.getPairByDonorId(donorId).subscribe({
      next: (pair) => {
        this.isDonorPartOfPair = pair !== null;
        this.updateAltruistCheckboxState();
      },
      error: (error) => {
        this.isDonorPartOfPair = false;
        this.updateAltruistCheckboxState();
      }
    });
  }

  updateAltruistCheckboxState(): void {
    const altruistControl = this.donorForm.get('altruist')!;
    if (this.isDonorPartOfPair && !this.donor?.altruist) {
      altruistControl.disable({ emitEvent: false });
    } else {
      altruistControl.enable({ emitEvent: false });
    }
  }

  private createPhenotypeValidator() {
    return (control: any) => {
      if (!control.value) return null; // Empty is valid
      return validatePhenotypepart(control.value) ? null : { invalidPhenotype: true };
    };
  }

  /**
   * Parse acceptable blood types from space-separated string to array
   * Handles both space-separated (new format) and comma-separated (legacy format)
   */
  private parseAcceptableBloodTypes(bloodTypes?: string): string[] {
    if (!bloodTypes) return [];

    // Split by space or comma, filter empty strings, and remove duplicates
    const types = bloodTypes
      .split(/[\s,]+/)
      .map(t => t.trim())
      .filter(t => t.length > 0);

    // Remove duplicates using Set
    return [...new Set(types)];
  }

  /**
   * Convert blood types array to space-separated string
   * Returns null for empty arrays
   */
  private formatAcceptableBloodTypes(bloodTypes: string[]): string | null {
    if (!bloodTypes || bloodTypes.length === 0) return null;
    return bloodTypes.join(' ');
  }

  /**
   * Split HLA string into Class I (A, B) and Class II (DR) components
   * Uses the same logic as ENIS import
   */
  private splitHla(fullHla: string): { hlaClassI: string; hlaClassII: string } {
    let hlaClassI = '';
    let hlaClassII = '';

    if (fullHla) {
      const tokens = fullHla.split(' ');
      const drIndex = tokens.findIndex((t) => t.startsWith('DR'));
      if (drIndex !== -1) {
        hlaClassI = tokens.slice(0, drIndex).join(' ');
        hlaClassII = tokens.slice(drIndex).join(' ');
      } else {
        hlaClassI = fullHla;
      }
    }

    return { hlaClassI, hlaClassII };
  }

  /**
   * Combine HLA Class I and Class II back into a single HLA string
   */
  private combineHla(hlaClassI: string, hlaClassII: string): string {
    const parts = [];
    if (hlaClassI && hlaClassI.trim()) {
      parts.push(hlaClassI.trim());
    }
    if (hlaClassII && hlaClassII.trim()) {
      parts.push(hlaClassII.trim());
    }
    return parts.join(' ');
  }

  /**
   * Apply field restrictions based on user role
   *
   * OPERATOR: Can edit all fields
   *
   * ALLOCATION: Can edit:
   *   - ENIS number
   *   - Date of birth
   *   - Center
   *   - Priority: Only enabled if it was initially checked (can uncheck it)
   *     - If initially unchecked, field is disabled (cannot check it)
   *     - Once unchecked, field becomes disabled (cannot check it again)
   *   - Can add/remove all privileges
   *   - All other fields are read-only
   *
   * CENTER: Can edit:
   *   - ENIS number
   *   - Date of birth
   *   - Can only remove privileges (not add)
   *   - Exception: "Delisted Unacceptables (Priority)" is editable only when the recipient has priority
   *   - All other fields are read-only
   */
  private applyFieldRestrictions(): void {
    console.log('[applyFieldRestrictions] Called', {
      isOperator: this.isOperator,
      isAllocation: this.isAllocation,
      isCenter: this.isCenter,
      donorFormExists: !!this.donorForm,
      recipientFormExists: !!this.recipientForm,
      formType: this.formType
    });

    // OPERATOR can edit all fields - no restrictions
    if (this.isOperator) {
      console.log('[applyFieldRestrictions] User is OPERATOR - no restrictions');
      return;
    }

    // ALLOCATION restrictions
    if (this.isAllocation) {
      console.log('[applyFieldRestrictions] Applying ALLOCATION restrictions');
      // Donor fields - only ENIS, DOB, Center editable
      // Donor privileges (limitedToCenter) are also editable (not disabled here)
      if (this.donorForm) {
        this.donorForm.get('firstName')?.disable({ emitEvent: false });
        this.donorForm.get('lastName')?.disable({ emitEvent: false });
        this.donorForm.get('gender')?.disable({ emitEvent: false });
        this.donorForm.get('abo')?.disable({ emitEvent: false });
        this.donorForm.get('hlaClassI')?.disable({ emitEvent: false });
        this.donorForm.get('hlaClassII')?.disable({ emitEvent: false });
        this.donorForm.get('altruist')?.disable({ emitEvent: false });
        this.donorForm.get('active')?.disable({ emitEvent: false });
        // Note: enisNumber, dob, center, and limitedToCenter remain enabled (editable)
      }

      // Recipient fields - only ENIS, DOB, Center, Priority (uncheck only), and all Privileges editable
      // All privilege fields (maxMismatches, maxMmA, maxMmB, maxMmDR, maxDonorAge, acceptableBloodTypes,
      // lowMfi1, lowMfi2, acceptableHla, extraWaitTime) are also editable (not disabled here)
      if (this.recipientForm) {
        this.recipientForm.get('firstName')?.disable({ emitEvent: false });
        this.recipientForm.get('lastName')?.disable({ emitEvent: false });
        this.recipientForm.get('gender')?.disable({ emitEvent: false });
        this.recipientForm.get('abo')?.disable({ emitEvent: false });
        this.recipientForm.get('abPhenotype')?.disable({ emitEvent: false });
        this.recipientForm.get('drPhenotype')?.disable({ emitEvent: false });
        this.recipientForm.get('urgency')?.disable({ emitEvent: false });
        this.recipientForm.get('primaryDiagnosis')?.disable({ emitEvent: false });
        this.recipientForm.get('dateFirstDialysis')?.disable({ emitEvent: false });
        this.recipientForm.get('dateScreening')?.disable({ emitEvent: false });
        this.recipientForm.get('unacceptables')?.disable({ emitEvent: false });
        this.recipientForm.get('previousPRA')?.disable({ emitEvent: false });
        this.recipientForm.get('virtualPRA')?.disable({ emitEvent: false });
        // Note: enisNumber, dob, center, and all privilege fields remain enabled (editable)

        // Priority: can only uncheck if it was initially checked
        // If priority was initially FALSE (unchecked), disable it so they can't check it
        // If priority was initially TRUE (checked), enable it so they can uncheck it
        if (this.initialPriorityValue) {
          // Priority was checked initially - allow unchecking
          this.recipientForm.get('priority')?.enable({ emitEvent: false });

          // Add listener to disable the field when unchecked
          this.recipientForm.get('priority')?.valueChanges.subscribe((value) => {
            if (!value && this.isAllocation) {
              // Priority was unchecked - disable it so they can't check it again
              this.recipientForm.get('priority')?.disable({ emitEvent: false });
            }
          });
        } else {
          // Priority was unchecked initially - disable it so they can't check it
          this.recipientForm.get('priority')?.disable({ emitEvent: false });
        }
      }
      return;
    }

    // CENTER restrictions
    if (this.isCenter) {
      // Donor fields - only ENIS, DOB editable
      if (this.donorForm) {
        this.donorForm.get('firstName')?.disable({ emitEvent: false });
        this.donorForm.get('lastName')?.disable({ emitEvent: false });
        this.donorForm.get('gender')?.disable({ emitEvent: false });
        this.donorForm.get('center')?.disable({ emitEvent: false });
        this.donorForm.get('abo')?.disable({ emitEvent: false });
        this.donorForm.get('hlaClassI')?.disable({ emitEvent: false });
        this.donorForm.get('hlaClassII')?.disable({ emitEvent: false });
        this.donorForm.get('altruist')?.disable({ emitEvent: false });
        this.donorForm.get('active')?.disable({ emitEvent: false });

        // Donor privileges: CENTER users can delete the limitedToCenter value (clear it)
        // If limitedToCenter has values, enable it so it can be cleared
        // If it's empty, disable it so new values can't be added
        this.applyCenterDonorPrivilegeFieldRestrictions();
      }

      // Recipient fields - only ENIS, DOB editable
      if (this.recipientForm) {
        this.recipientForm.get('firstName')?.disable({ emitEvent: false });
        this.recipientForm.get('lastName')?.disable({ emitEvent: false });
        this.recipientForm.get('gender')?.disable({ emitEvent: false });
        this.recipientForm.get('center')?.disable({ emitEvent: false });
        this.recipientForm.get('abo')?.disable({ emitEvent: false });
        this.recipientForm.get('abPhenotype')?.disable({ emitEvent: false });
        this.recipientForm.get('drPhenotype')?.disable({ emitEvent: false });
        this.recipientForm.get('urgency')?.disable({ emitEvent: false });
        this.recipientForm.get('primaryDiagnosis')?.disable({ emitEvent: false });
        this.recipientForm.get('dateFirstDialysis')?.disable({ emitEvent: false });
        this.recipientForm.get('dateScreening')?.disable({ emitEvent: false });
        this.recipientForm.get('unacceptables')?.disable({ emitEvent: false });
        this.recipientForm.get('previousPRA')?.disable({ emitEvent: false });
        this.recipientForm.get('virtualPRA')?.disable({ emitEvent: false });

        // Priority: completely read-only for centers
        this.recipientForm.get('priority')?.disable({ emitEvent: false });

        // Privileges: CENTER users can delete individual privilege fields (clear them)
        // If privileges exist, enable the fields so they can be cleared
        // If no privileges exist, fields remain disabled
        // This will be handled by applyCenterPrivilegeFieldRestrictions() after checking if privileges exist
        this.applyCenterPrivilegeFieldRestrictions();
      }
      return;
    }
  }

  /**
   * Apply privilege field restrictions for CENTER users
   * CENTER users can add/remove all privilege values freely
   * EXCEPTION: "Delisted Unacceptables (Priority)" is editable only when the recipient has priority
   */
  private applyCenterPrivilegeFieldRestrictions(): void {
    if (!this.isCenter || !this.recipientForm) {
      return;
    }

    // Enable all privilege fields for CENTER users (they can add/remove freely)
    this.recipientForm.get('extraWaitTime')?.enable({ emitEvent: false });
    this.recipientForm.get('maxDonorAge')?.enable({ emitEvent: false });
    this.recipientForm.get('maxMismatches')?.enable({ emitEvent: false });
    this.recipientForm.get('maxMmA')?.enable({ emitEvent: false });
    this.recipientForm.get('maxMmB')?.enable({ emitEvent: false });
    this.recipientForm.get('maxMmDR')?.enable({ emitEvent: false });
    this.recipientForm.get('acceptableBloodTypes')?.enable({ emitEvent: false });
    this.recipientForm.get('lowMfi1')?.enable({ emitEvent: false });
    this.recipientForm.get('lowMfi2')?.enable({ emitEvent: false });

    // Delisted Unacceptables (Priority): CENTER users can edit this only when the recipient has priority.
    // (e.g. after the Crossover+ committee has approved a priority request for this recipient)
    this.canEditAcceptableHla = !!this.recipient?.priority;
    const acceptableHlaControl = this.recipientForm.get('acceptableHla');
    if (this.canEditAcceptableHla) {
      acceptableHlaControl?.enable({ emitEvent: false });
    } else {
      acceptableHlaControl?.disable({ emitEvent: false });
    }
  }

  /**
   * Apply donor privilege field restrictions for CENTER users
   * CENTER users can freely add/remove donor privilege values (limitedToCenter)
   */
  private applyCenterDonorPrivilegeFieldRestrictions(): void {
    if (!this.isCenter || !this.donorForm) {
      return;
    }

    const limitedToCenterControl = this.donorForm.get('limitedToCenter');
    // Enable limitedToCenter field for CENTER users (they can add/remove freely)
    limitedToCenterControl?.enable({ emitEvent: false });
  }
}
